import React, { useState, useContext } from 'react'

import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { Redirect } from 'react-router-dom'
import { Formik } from 'formik'
import { Form } from 'reactstrap'

import BaseContainer from '../common/BaseContainer'
import ControlHeader from '../common/ControlHeader'
import PrimaryButton from '../common/buttons/PrimaryButton'
import Loading from '../common/icons/Loading'
import generateFormikInputs from '../../utils/generateFormikInputs'
import { defaultLink } from '../../utils/consts'
import fetch from '../../utils/fetcher'
import useFetch from '../../utils/useFetch'
import { AuthUserContext } from '../common/Session';
import FocusOnFirstError from '../common/Formik/FocusOnFirstError'
import { SuccessMessage, ErrorMessage, useVisible } from '../common/AlertManager'
import {
  validationSchema,
  formSwitchesData,
  formImagesData,
  formOptionsData,
  baseUrl,
  transformServerData,
  transformDataToSendToServer,
} from './config';
import useStyles from './styles'
import { ModalManagerContext } from '../ModalsManager'


const Portal = ({ create }) => {
  const { t } = useTranslation(['translation', 'COMMON'])
  const [redirect, setRedirect] = useState(false)
  const [alertError, setAlertError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const { visible: isErrorVisible, open: openError } = useVisible(false)
  const { visible: isSuccessMessageVisible, open: openSuccessMessage } = useVisible(false)
  const classes = useStyles()

  const openModal = useContext(ModalManagerContext)
  const { render } = useFetch(baseUrl, openModal)

  const onSubmit = (values, actions) => {
    const url = `${baseUrl}/edit`
    fetch(url, transformDataToSendToServer(values)).then(() => {
      setAlertError(null)
      setSuccessMessage(t('COMMON.SUCCESSFUL_UPDATE'))
      openSuccessMessage()
      if (create) {
        setRedirect(true);
      }
    })
    .then(actions.fetchAvatar)
    .catch(e => {
      if (e.errorCode === '0000') {
        actions.setFieldError('facebookLogin', t('PORTAL.DISABLE_FACEBOOK'))
      }
      setAlertError(e)
      openError()
      setSuccessMessage(null)
    })
    .finally(() => {
      actions.setSubmitting(false)
    })
  }

  if (redirect) {
    return <Redirect to={defaultLink} />;
  }

  return (
    <AuthUserContext.Consumer>
      {({ fetchAvatar }) => (
        <BaseContainer>
          <ControlHeader
            title={create ? t('PORTAL.CREATE_PORTAL') : t('PORTAL.EDIT_PORTAL')}
            showTabs={!create}
            activeTab="portal"
          />
          {render(renderData => (
            <Formik
              initialValues={transformServerData(renderData)}
              onSubmit={(values, actions) => onSubmit(values, { ...actions, fetchAvatar })}
              validationSchema={validationSchema}
            >
              {({
                handleSubmit,
                isSubmitting,
                ...formikProps
              }) => (
                <Form onSubmit={handleSubmit}>
                  <div className={classes.inputsGrid}>
                    <div className={classes.switchesGrid}>
                      {generateFormikInputs(formSwitchesData, formikProps, false, isSubmitting)}
                    </div>

                    <div className={classes.images}>
                      {generateFormikInputs(formImagesData, formikProps, false, isSubmitting)}
                    </div>

                    <div className={classes.options}>
                      <h4 className={classes.optionsHeader}>{t('PORTAL.OPTIONS')}</h4>
                      {generateFormikInputs(formOptionsData, formikProps, false, isSubmitting || !formikProps.values.manualLogin)}
                    </div>

                    <PrimaryButton type="submit" className={classes.button} disabled={isSubmitting} >
                      {t('PORTAL.SAVE_BUTTON')}
                    </PrimaryButton>

                  </div>

                  <ErrorMessage error={alertError} visible={isErrorVisible} />
                  { successMessage && <SuccessMessage message={successMessage} visible={isSuccessMessageVisible} /> }
                  { isSubmitting && <Loading /> }

                  <FocusOnFirstError />
                </Form>
              )}
            </Formik>
          ))}
        </BaseContainer>
      )}
    </AuthUserContext.Consumer>
  );
};

Portal.defaultProps = {
  create: false,
};

Portal.propTypes = {
  create: PropTypes.bool,
};

export default Portal;
